// skipcq: JS-W1028
import { useCallback, useEffect, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SportsBarOutlinedIcon from '@mui/icons-material/SportsBarOutlined';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import BrewhousePageShell from '../../components/brewhouse/BrewhousePageShell';
import { fetchBreweryClaimsForCurrentUser } from '../../services/breweryClaims';

function formatClaimStatusLabel(entry) {
  return typeof entry.status === 'string'
    ? entry.status.charAt(0).toUpperCase() + entry.status.slice(1)
    : '';
}

function formatClaimBreweryLine(entry) {
  let name;
  if (typeof entry.breweryName === 'string' && entry.breweryName.trim().length > 0) {
    name = entry.breweryName.trim();
  } else if (entry.breweryId != null && String(entry.breweryId).trim().length > 0) {
    name = String(entry.breweryId).trim();
  } else {
    name = 'Unknown brewery';
  }
  const city = typeof entry.breweryCity === 'string' ? entry.breweryCity.trim() : '';
  const stateAbbrev = typeof entry.breweryState === 'string' ? entry.breweryState.trim() : '';
  const place = city && stateAbbrev ? `${city}, ${stateAbbrev}` : city || stateAbbrev;
  if (place) {
    return `${name} — ${place}`;
  }
  return name;
}

function StatusStageColumn({ IconComponent, stepNumber, label, highlighted }) {
  const iconColor = highlighted ? 'primary.main' : 'text.secondary';
  return (
    <Stack alignItems="center" spacing={1.25} sx={{ minWidth: { xs: 88, sm: 104 } }}>
      <IconComponent
        sx={{
          fontSize: { xs: 44, sm: 52 },
          color: iconColor,
        }}
        aria-hidden
      />
      <Avatar
        sx={{
          bgcolor: highlighted ? 'primary.main' : 'action.selected',
          color: highlighted ? 'primary.contrastText' : 'text.primary',
          fontWeight: 600,
          width: 36,
          height: 36,
        }}
      >
        {stepNumber}
      </Avatar>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

function ClaimStatusClaimsColumn({ rows }) {
  if (rows.length === 0) {
    return (
      <Stack spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          No claims yet. Use Claim in the left navigation to get started.
        </Typography>
      </Stack>
    );
  }
  return (
    <TableContainer sx={{ width: '100%', textAlign: 'left' }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableBody>
          {rows.map((rowEntry) => (
            <TableRow key={rowEntry.id}>
              <TableCell
                sx={{
                  typography: 'body1',
                  fontWeight: 700,
                  verticalAlign: 'top',
                  width: '25%',
                  borderBottom: 1,
                  borderColor: 'divider',
                  pr: 2,
                }}
              >
                {formatClaimStatusLabel(rowEntry)}
              </TableCell>
              <TableCell
                sx={{
                  typography: 'body1',
                  verticalAlign: 'top',
                  borderBottom: 1,
                  borderColor: 'divider',
                  wordBreak: 'break-word',
                }}
              >
                {formatClaimBreweryLine(rowEntry)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ClaimStatusSummarySection({ rows }) {
  return (
    <Stack spacing={1} sx={{ mb: 4, alignItems: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 560 }}>
        <ClaimStatusClaimsColumn rows={rows} />
      </Box>
    </Stack>
  );
}

function ClaimStatusStepStrip({ highlightStepTwo }) {
  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      sx={{ mb: 4, flexWrap: 'wrap', rowGap: 2, columnGap: 0.5 }}
    >
      <StatusStageColumn
        IconComponent={SearchIcon}
        stepNumber={1}
        label="Find"
        highlighted={false}
      />
      <ArrowForwardIcon color="disabled" sx={{ mx: 0.5 }} />
      <StatusStageColumn
        IconComponent={DescriptionOutlinedIcon}
        stepNumber={2}
        label="Verify"
        highlighted={highlightStepTwo}
      />
      <ArrowForwardIcon color="disabled" sx={{ mx: 0.5 }} />
      <StatusStageColumn
        IconComponent={SportsBarOutlinedIcon}
        stepNumber={3}
        label="Manage"
        highlighted={false}
      />
    </Stack>
  );
}

function ClaimStatusNarrativeBlock() {
  return (
    <>
      <Typography variant="body1" paragraph sx={{ textAlign: 'center' }}>
        Thanks for stepping up to manage your brewery! Your updates are going to make the app a whole lot better,
        and our lives a whole lot easier.
      </Typography>
      <Typography variant="body1" paragraph sx={{ textAlign: 'center' }}>
        {'We\'re currently reviewing your request. Please give us a few business days to review your docs,'}
        {' remember, there are only two of us running this show, and we still have to find time to actually'}
        {' drink beer. We\'ll email you the moment your account status changes.'}
      </Typography>
      <Typography variant="body1" paragraph sx={{ textAlign: 'center' }}>
        Stay tuned. – Cheers!
      </Typography>
    </>
  );
}

function ClaimStatusSupportFooter() {
  return (
    <Typography component="div" variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
      Any questions or concerns — do not hesitate to email us at{' '}
      <Box component="a" href="mailto:Support@idtapthat.us" sx={{ color: 'inherit' }}>
        Support@idtapthat.us
      </Box>
    </Typography>
  );
}

/** Card layout for resolved status — subsection components keep nesting shallow in the content component. */
function BreweryClaimStatusMainCard({ rows, highlightStepTwo, fetchError, onRetryFetch }) {
  return (
    <Card
      variant="outlined"
      sx={{
        maxWidth: 720,
        mx: 'auto',
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        {fetchError ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={(
              <Button color="inherit" size="small" type="button" onClick={onRetryFetch}>
                Retry
              </Button>
            )}
          >
            {fetchError}
          </Alert>
        ) : null}
        <ClaimStatusSummarySection rows={rows} />
        <ClaimStatusStepStrip highlightStepTwo={highlightStepTwo} />
        <ClaimStatusNarrativeBlock />
        <ClaimStatusSupportFooter />
      </CardContent>
    </Card>
  );
}

function BreweryClaimStatusContent() {
  const [busy, setBusy] = useState(true);
  const [rows, setRows] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setFetchError(null);
    (async () => {
      try {
        const fetched = await fetchBreweryClaimsForCurrentUser();
        if (!cancelled) {
          setRows(fetched);
        }
      } catch (error) {
        console.error('BreweryClaimStatusPage: fetch failed', error);
        if (!cancelled) {
          setFetchError(
            typeof error?.message === 'string'
              ? error.message
              : 'Unable to load claim status. Please try again.',
          );
        }
      } finally {
        if (!cancelled) {
          setBusy(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  const highlightStepTwo = rows.some((row) => row.status === 'pending');

  const handleRetryFetch = useCallback(function handleRetryFetch() {
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  if (busy) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError && rows.length === 0) {
    return (
      <BrewhousePageShell
        breadcrumbs={[
          { label: 'Claim', to: '/brewery-portal/claim' },
          { label: 'Claim Status' },
        ]}
        backLink={{ to: '/brewery-portal/claim', label: 'Back to Claim' }}
        title="Claim status"
        maxWidth={480}
        sx={{ textAlign: 'center' }}
      >
        <Stack spacing={2} alignItems="center">
          <Typography variant="body1" color="text.secondary">
            {fetchError}
          </Typography>
          <Button type="button" variant="contained" color="secondary" onClick={handleRetryFetch}>
            Retry
          </Button>
        </Stack>
      </BrewhousePageShell>
    );
  }

  return (
    <BrewhousePageShell
      breadcrumbs={[
        { label: 'Claim', to: '/brewery-portal/claim' },
        { label: 'Claim Status' },
      ]}
      backLink={{ to: '/brewery-portal/claim', label: 'Back to Claim' }}
      title="Claim status"
      maxWidth={720}
    >
      <BreweryClaimStatusMainCard
        rows={rows}
        highlightStepTwo={highlightStepTwo}
        fetchError={fetchError}
        onRetryFetch={handleRetryFetch}
      />
    </BrewhousePageShell>
  );
}

export default function BreweryClaimStatusPage() {
  return <BreweryClaimStatusContent />;
}
